import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { CaretIconComponent } from '../caret-icon/caret-icon.component';
import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';
import { normalizeSearchText } from '../../utils/text-normalization.util';

/**
 * Represents a single item in the dropdown
 */
export interface DropdownItem {
  /**
   * Unique identifier for the item
   */
  id: string;
  /**
   * Display label for the item
   */
  label: string;
  /**
   * Optional secondary text (e.g., count, total, metadata)
   */
  secondaryText?: string;
  /**
   * Optional translation key for prefix to display before secondaryText
   * When provided, the prefix will be translated and displayed as: "{{ prefix | translate }} : {{ secondaryText }}"
   */
  secondaryTextPrefix?: string;
  /**
   * Optional custom data associated with the item
   */
  data?: any;
  /**
   * Optional query params to pass when navigating via item.data
   */
  queryParams?: Record<string, string>;
  /**
   * Whether this item is a child/sub-item rendered with indentation
   */
  isChild?: boolean;
  /**
   * Optional child items that expand inline beneath this item when its caret is clicked
   */
  children?: DropdownItem[];
}

/**
 * A dropdown component that displays a list of selectable items.
 * Designed based on Figma design with country names support.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-14542
 *
 * @example
 * ```html
 * <app-dropdown
 *   [items]="countries"
 *   [isOpen]="dropdownOpen"
 *   (itemSelected)="onCountrySelected($event)"
 *   (closed)="dropdownOpen = false">
 * </app-dropdown>
 * ```
 */
@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, CaretIconComponent, TranslateLangPipe],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent {
  /**
   * Array of items to display in the dropdown
   */
  items = input<DropdownItem[]>([]);

  /**
   * Whether the dropdown is currently visible
   */
  isOpen = input<boolean>(false);

  /**
   * Currently selected item
   */
  selectedItem = input<DropdownItem | null>(null);

  /**
   * Maximum height for the dropdown before scrolling
   */
  maxHeight = input<string>('400px');

  /**
   * Whether the dropdown opens above its trigger element
   */
  openAbove = input<boolean>(false);

  /**
   * Whether to show a search box that filters items by label
   */
  searchable = input<boolean>(false);

  /**
   * Placeholder text for the search box (when searchable)
   */
  searchPlaceholder = input<string>('Search...');

  /**
   * Label shown when a search yields no matching items
   */
  noResultsLabel = input<string>('No results found');

  /**
   * Emits when a user selects an item from the dropdown
   */
  itemSelected = output<DropdownItem>();

  /**
   * Emits when the dropdown should close
   */
  closed = output<void>();

  /**
   * Reference to the dropdown container element
   */
  dropdownContainer = viewChild<ElementRef>('dropdownContainer');

  /**
   * Reference to the search input element (when searchable)
   */
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  /**
   * Current search term used to filter items
   */
  searchText = signal<string>('');

  /**
   * Index of the currently focused item (for keyboard navigation)
   */
  focusedIndex = signal<number>(-1);

  /**
   * Index of the item whose inline submenu is currently expanded (-1 when none)
   */
  expandedSubmenuIndex = signal<number>(-1);

  /**
   * Items to render — filtered by the search term when searchable.
   * Separators are dropped while a search term is active.
   */
  visibleItems = computed(() => {
    const term = normalizeSearchText(this.searchText().trim());
    const all = this.items();
    if (!this.searchable() || !term) {
      return all;
    }
    return all.filter(
      (item) => item.id !== '__separator__' && normalizeSearchText(item.label ?? '').includes(term)
    );
  });

  /**
   * Computed property to get the currently focused item
   */
  focusedItem = computed(() => {
    const index = this.focusedIndex();
    const itemsList = this.visibleItems();
    return index >= 0 && index < itemsList.length ? itemsList[index] : null;
  });

  constructor() {
    // Reset focused index, active submenu and search term when dropdown closes;
    // focus the search box when it opens (if searchable).
    effect(() => {
      if (!this.isOpen()) {
        this.focusedIndex.set(-1);
        this.expandedSubmenuIndex.set(-1);
        this.searchText.set('');
      } else if (this.searchable()) {
        setTimeout(() => this.searchInput()?.nativeElement?.focus(), 0);
      }
    });

    // Scroll to focused item when focusedIndex changes
    effect(() => {
      const index = this.focusedIndex();
      const container = this.dropdownContainer();

      if (index >= 0 && container) {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          const items = container.nativeElement.querySelectorAll(
            '.dropdown__item, .dropdown__separator'
          );
          const focusedItem = items[index] as HTMLElement;

          if (focusedItem) {
            focusedItem.scrollIntoView({
              block: 'nearest',
              inline: 'nearest',
            });
          }
        }, 0);
      }
    });

    // Detect scrollbar and add class when dropdown is open
    effect(() => {
      if (this.isOpen()) {
        const container = this.dropdownContainer();
        if (container) {
          setTimeout(() => {
            const element = container.nativeElement;
            // Check if element has scrollbar (scrollHeight > clientHeight)
            if (element.scrollHeight > element.clientHeight) {
              element.classList.add('has-scrollbar');
            } else {
              element.classList.remove('has-scrollbar');
            }
          }, 0);
        }
      }
    });
  }

  /**
   * Handles item selection
   */
  selectItem(item: DropdownItem): void {
    if (item.id === '__separator__') return;
    this.itemSelected.emit(item);
    this.closed.emit();
  }

  /**
   * Handles keyboard navigation
   */
  onKeyDown(event: KeyboardEvent): void {
    const itemsList = this.visibleItems();
    const currentIndex = this.focusedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextUnselectedItem(currentIndex, itemsList, 'down');
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusNextUnselectedItem(currentIndex, itemsList, 'up');
        break;

      case 'Enter':
        event.preventDefault();
        const focused = this.focusedItem();
        if (focused) {
          this.selectItem(focused);
        }
        break;

      case 'ArrowRight':
        if (this.focusedItem()?.children?.length) {
          event.preventDefault();
          this.expandedSubmenuIndex.set(currentIndex);
        }
        break;

      case 'ArrowLeft':
        if (this.isSubmenuExpanded(currentIndex)) {
          event.preventDefault();
          this.expandedSubmenuIndex.set(-1);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.closed.emit();
        break;

      default:
        break;
    }
  }

  /**
   * Finds and focuses the next unselected item in the given direction
   * Supports circular navigation (wraps around to the other end)
   */
  private focusNextUnselectedItem(
    currentIndex: number,
    itemsList: DropdownItem[],
    direction: 'up' | 'down'
  ): void {
    const selectedItem = this.selectedItem();
    const increment = direction === 'down' ? 1 : -1;
    let nextIndex = currentIndex + increment;

    // Handle wrapping around
    if (nextIndex < 0) {
      nextIndex = itemsList.length - 1;
    } else if (nextIndex >= itemsList.length) {
      nextIndex = 0;
    }

    // Keep track of where we started to avoid infinite loops
    const startIndex = nextIndex;
    let attempts = 0;
    const maxAttempts = itemsList.length;

    // Find the next unselected item
    while (attempts < maxAttempts) {
      const item = itemsList[nextIndex];

      // Skip separator items and the selected item
      if (item.id === '__separator__' || (selectedItem && item.id === selectedItem.id)) {
        nextIndex += increment;

        // Handle wrapping around during search
        if (nextIndex < 0) {
          nextIndex = itemsList.length - 1;
        } else if (nextIndex >= itemsList.length) {
          nextIndex = 0;
        }

        attempts++;
        continue;
      }

      // Found an unselected item
      this.focusedIndex.set(nextIndex);
      return;
    }

    // If we've checked all items and they're all selected (unlikely),
    // stay at the current position
  }

  /**
   * Checks if an item is currently selected
   */
  isItemSelected(item: DropdownItem): boolean {
    const selected = this.selectedItem();
    return selected ? selected.id === item.id : false;
  }

  /**
   * Checks if an item is currently focused
   */
  isItemFocused(index: number): boolean {
    return this.focusedIndex() === index;
  }

  /**
   * Handles mouse enter on an item
   */
  onItemMouseEnter(index: number): void {
    if (this.visibleItems()[index]?.id === '__separator__') return;
    this.focusedIndex.set(index);
  }

  /**
   * Whether the inline submenu of the item at the given index is expanded
   */
  isSubmenuExpanded(index: number): boolean {
    return this.expandedSubmenuIndex() === index;
  }

  /**
   * Expands the inline submenu of the item at the given index, collapsing any other.
   * Only one submenu is open at a time, matching the mobile menu.
   */
  toggleSubmenu(index: number): void {
    this.expandedSubmenuIndex.update((current) => (current === index ? -1 : index));
  }

  /**
   * Updates the search term as the user types in the search box
   */
  onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
    this.focusedIndex.set(-1);
    // Indices shift as the list is filtered, so collapse any expanded submenu
    this.expandedSubmenuIndex.set(-1);
  }
}
